import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";
import { Feature } from "models/api";
import { ITopic, ITopicInput } from "models/topic";
import React, { useState } from "react";
import { QueryClient, useMutation } from "react-query";

import {
  TopicCells,
  addTopic,
  deleteTopic,
  editTopic,
  getTopics,
} from "framework/topics";
import AddTopic from "./partials/AddTopic/AddTopic";
import EditTopic from "./partials/EditTopic/EditTopic";
import DeleteTopic from "./partials/DeleteTopic/DeleteTopic";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import { useTranslation } from "react-i18next";
import BlockIcon from "@mui/icons-material/Block";
import { getPermission, getUserData } from "helpers";
import useFetchSection from "hooks/useFetchSection";
interface Props {
  feature: Feature;
}

const Topics: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const { t, i18n } = useTranslation();
  const [FilterTopics, setFilterTopics] = useState<ITopic[]>([] as ITopic[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedTopic, setSelectedTopic] = useState<ITopic>({} as ITopic);
  const [inputObj, setInputObj] = useState<ITopicInput>({} as ITopicInput);
  const { setAlert } = useAlert();
  const user = getUserData();
  const { colors } = user;
  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addTopicsMutation = useMutation({
    mutationFn: (createInput: ITopicInput) => {
      return addTopic(createInput);
    },
  });

  const editTopicsMutation = useMutation({
    mutationFn: (data: { createInput: ITopicInput; id: string }) => {
      return editTopic(data.createInput, data.id);
    },
  });

  const deleteTopicsMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteTopic(id);
    },
  });

  const {
    loading,
    data: Topics,
    error,
    permission,
    revalidate,
  } = useFetchSection({
    fetchData: () => getTopics(),
    fetchPermission: () => getPermission(9),
    section: "Topics",
    onSuccess: (data) => setFilterTopics(data),
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    setInputObj((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);

  const handleOpenEdit = (Topic: ITopic) => {
    setSelectedTopic(Topic);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedTopic({} as ITopic);
  };

  const handleOpenDelete = (Topic: ITopic) => {
    setSelectedTopic(Topic);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedTopic({} as ITopic);
  };

  const getBodyCells = (row: ITopic) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "name")
        return {
          id: "name",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.name}</Typography>
            </TableCell>
          ),
        };

      if (key === "nameAr")
        return {
          id: "nameAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.nameAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "description")
        return {
          id: "description",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">
                {i18n.language === "en" ? row?.description : row?.descriptionAr}
              </Typography>
            </TableCell>
          ),
        };

      if (key === "descriptionAr")
        return {
          id: "descriptionAr",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.descriptionAr}</Typography>
            </TableCell>
          ),
        };

      if (key === "id")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
                justifyContent={i18n.language === "en" ? "start" : "end"}
              >
                {permission?.hasModifyPermission && (
                  <Tooltip title={t("T_edit")}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>
                )}

                {permission?.hasDeletePermission && (
                  <Tooltip title={t("T_delete")}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: colors.deleteIconColor
                          ? colors.deleteIconColor
                          : "red",
                        "&:hover": { color: colors?.deleteIconHoverColor },
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {
    if (
      !inputObj.name ||
      !inputObj.nameAr ||
      !inputObj.description ||
      !inputObj.descriptionAr
    )
      return setAlert({
        open: true,
        message: "Please fill all fields",
        type: "error",
      });
    try {
      setLoading2(true);

      await addTopicsMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Topic added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setInputObj({} as ITopicInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    try {
      setLoading2(true);
      let createInput: ITopicInput = {
        name: inputObj.name || selectedTopic.name,
        description: inputObj.description || selectedTopic.description,
        nameAr: inputObj.nameAr || selectedTopic.nameAr,
        descriptionAr: inputObj.descriptionAr || selectedTopic.descriptionAr,
      };
      await editTopicsMutation.mutateAsync({
        createInput,
        id: selectedTopic.id,
      });
      setAlert({
        open: true,
        message: "Topic updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      setInputObj({} as ITopicInput);
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteTopicsMutation.mutateAsync(selectedTopic.id);
      setAlert({
        open: true,
        message: "Topic deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterTopics(Topics!);
    let filtered = Topics!.filter((city) =>
      city.name?.toLowerCase()?.includes(value.toLowerCase())
      || city.nameAr?.toLowerCase()?.includes(value.toLowerCase())
      || city.description?.toLowerCase()?.includes(value.toLowerCase())
      || city.descriptionAr?.toLowerCase()?.includes(value.toLowerCase())
    );
    setFilterTopics(filtered);
  };

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Typography
            variant="h3"
            color={colors?.labelColor ? colors?.labelColor : "header.primary"}
          >
            {t(feature.name)}
          </Typography>
          <Stack direction={"row"} spacing={1}>
            <Search handleChange={handleSearch} />
            {permission?.hasModifyPermission && (
              <Tooltip title="Add Topic">
                <IconButton
                  sx={{
                    color: colors.addUpdateIconColor
                      ? colors.addUpdateIconColor
                      : "red",
                    "&:hover": { color: colors?.addUpdateIconHoverColor },
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>

        {permission?.hasReadPermission! && FilterTopics.length > 0 && (
          <List
            headCells={TopicCells}
            data={FilterTopics}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterTopics || FilterTopics.length === 0) &&
          permission?.hasReadPermission! && (
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={t("T_no_topic_header")}
              message={t("T_no_topic_message")}
            />
          )}

        {!permission?.hasReadPermission && (
          <EmptyContent
            icon={<BlockIcon sx={{ fontSize: "3.5rem " }} />}
            header={t("T_no_permissions")}
            message={t("T_no_permissions_message")}
          />
        )}
      </Stack>

      {openAdd && (
        <AddTopic
          open={openAdd}
          loading={loading2}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditTopic
          open={openEdit}
          loading={loading2}
          topic={selectedTopic}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}
      {openDelete && (
        <DeleteTopic
          open={openDelete}
          loading={loading2}
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}
    </>
  );
};

export default Topics;
